import {
  Card,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Textarea,
} from "@chakra-ui/react";
import FormSelect from "../Widgets/FormSelect";
import { FaSms, FaWpforms } from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io5";
import { Select } from "chakra-react-select";
import IconWidgetOption from "../Playground/IconWidgetOption";
import FormLabelWithInfoTooltip from "../Widgets/FormLabelWithInfoTooltip";
import { useEffect, useState } from "react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import useWidgetType from "./useWidgetType";

const convoBridgeTooltip = `Lets you collect fields like email or mailing address from text channels such as WhatsApp or SMS. This ensures much higher accuracy of data in comparison to voice calls`;
const formTooltip =
  "Pick the relevant Form that will be fetched from secondary conversation by the primary bot.";
const bridgedWidgetTooltip =
  "An outbound message will be triggered using this Widget to start the secondary conversation and make a bridge to the primary bot.";
const whatsAppMsgTooltip =
  " WhatsApp doesn’t allow initiating conversations with a free text body form if user is not active in the past 24 hours. Hence we encourage to use a template configured in your WhatsApp Business Account to trigger the conversation.";
const messageBodyTooltip = `Must have {{form_link}} which will be replaced with the actual form URL where the user can fill up the Form`;
const handoffInterruptionTooltip =
  "As your second assistant is talking with the user, it will feed the captured form fields back into the parent assistant, if enabled.";

export default function SmartHandoff({ toolUserForm }) {
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = toolUserForm;
  const { widgetTypesOpt, status } = useWidgetType();

  const formError = errors?.credentials?.configuration?.smart_handoff?.form_id;
  const handoffWidgetError =
    errors?.credentials?.configuration?.smart_handoff?.sub_widget_id;

  const [handoffWidgetType, setHandoffWidgetType] = useState("");
  const [bodyParameters, setBodyParameters] = useState([]);

  const selectedHandoffType =
    watch("credentials.configuration.smart_handoff.type") ||
    setValue("credentials.configuration.smart_handoff.type", "conversational");

  const selectedWhatsAppType =
    handoffWidgetType === "whatsapp"
      ? watch("credentials.configuration.smart_handoff.whatsapp.type") ||
        setValue(
          "credentials.configuration.smart_handoff.whatsapp.type",
          "text"
        )
      : "";

  const handoffInterruptionStatus = watch(
    "credentials.configuration.smart_handoff.enable_handoff_interruption"
  );

  const handoffTypeWidget = {
    web_form: ["render_form"],
    conversational: ["sms", "whatsapp"],
  };

  const handoffWidgetTypeIcon = {
    render_form: FaWpforms,
    whatsapp: IoLogoWhatsapp,
    sms: FaSms,
  };

  const handoffWidgetOptions = handoffTypeWidget[selectedHandoffType]
    ?.reduce((acc, type) => {
      const widgetOptions =
        widgetTypesOpt && widgetTypesOpt[type] ? widgetTypesOpt[type] : [];
      return acc.length ? [...acc, ...widgetOptions] : [...widgetOptions];
    }, [])
    ?.map((option) => ({
      ...option,
      Icon: handoffWidgetTypeIcon[option?.data?.widget_type],
    }));

  const handleHandOffTypeSelect = (value) => {
    const obj = {
      type: value,
      form_id: watch("credentials.configuration.smart_handoff.form_id"),
      sub_widget_id: "",
      message_body: "",
    };
    setValue("credentials.configuration.smart_handoff", obj);
    setHandoffWidgetType("");
  };

  const handleInterruptionStatus = (e) => {
    setValue(
      "credentials.configuration.smart_handoff.enable_handoff_interruption",
      e.target.checked
    );
  };

  const handleFormSelect = (selectedForm) => {
    setValue(
      "credentials.configuration.smart_handoff.form_id",
      selectedForm?.value?.id || ""
    );
  };

  const handleHandoffWidgetSelect = (selectedOpt) => {
    setValue(
      "credentials.configuration.smart_handoff.sub_widget_id",
      selectedOpt.value
    );
    setHandoffWidgetType(selectedOpt.data.widget_type);
  };

  const handleWhatsAppTypeSelect = (value) => {
    const obj = {
      ...watch("credentials.configuration.smart_handoff"),
      whatsapp: { type: value },
      message_body: "",
    };
    setValue("credentials.configuration.smart_handoff", obj);
  };

  const handleAddParameter = () => {
    const parameters = [...bodyParameters];
    parameters.push("");
    setBodyParameters(parameters);
    setValue(
      "credentials.configuration.smart_handoff.whatsapp.body_parameters",
      parameters
    );
  };

  const handleRemoveParameter = (index) => {
    const parameters = [...bodyParameters];
    parameters.splice(index, 1);
    setBodyParameters(parameters);
    setValue(
      "credentials.configuration.smart_handoff.whatsapp.body_parameters",
      parameters
    );
  };

  const isTemplateType =
    watch("credentials.configuration.smart_handoff.whatsapp.type") ===
    "template";

  const selectedHandoffWidget = handoffWidgetOptions?.find(
    (option) =>
      option.value ===
      watch("credentials.configuration.smart_handoff.sub_widget_id")
  );

  const selectedHandoffForm = watch(
    "credentials.configuration.smart_handoff.form_id"
  );

  useEffect(() => {
    const parameters = watch(
      "credentials.configuration.smart_handoff.whatsapp.body_parameters"
    );
    if (parameters) {
      setBodyParameters(parameters);
    }
  }, []);

  return (
    <Stack spacing={2}>
      <FormLabelWithInfoTooltip
        label={"Convo Bridge"}
        tip={convoBridgeTooltip}
      />

      <Card variant={"outline"} gap={2} p={2}>
        <FormControl display={"flex"} flexDirection={"row"}>
          <FormLabel>Type: </FormLabel>
          <RadioGroup
            onChange={handleHandOffTypeSelect}
            value={selectedHandoffType}
          >
            <Stack direction="row">
              <Radio value="web_form">Web form</Radio>
              <Radio value="conversational">Conversational</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <FormControl display={"flex"} flexDirection={"row"}>
          <FormLabelWithInfoTooltip
            label={"Enable Handoff Interruption"}
            tip={handoffInterruptionTooltip}
          />
          <Switch
            onChange={handleInterruptionStatus}
            isChecked={handoffInterruptionStatus}
          />
        </FormControl>

        <FormControl isInvalid={handoffWidgetError}>
          <FormLabelWithInfoTooltip
            label={"Bridged Widget"}
            tip={bridgedWidgetTooltip}
          />
          <Select
            value={selectedHandoffWidget || {}}
            options={handoffWidgetOptions}
            components={{ Option: IconWidgetOption }}
            onChange={handleHandoffWidgetSelect}
            isLoading={status === "loading"}
          />
          <FormErrorMessage>
            {handoffWidgetError && handoffWidgetError.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={formError}>
          <FormLabelWithInfoTooltip label={"Form"} tip={formTooltip} />
          <FormSelect
            selectedForm={selectedHandoffForm}
            onChange={handleFormSelect}
          />
          <FormErrorMessage>{formError && formError.message}</FormErrorMessage>
        </FormControl>

        {handoffWidgetType === "whatsapp" && (
          <FormControl display={"flex"} flexDirection={"row"}>
            <FormLabelWithInfoTooltip
              label={"WhatsApp message type:"}
              tip={whatsAppMsgTooltip}
            />
            <RadioGroup
              onChange={handleWhatsAppTypeSelect}
              value={selectedWhatsAppType}
            >
              <Stack direction="row">
                <Radio value="text">Text</Radio>
                <Radio value="template">Template</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
        )}

        {!isTemplateType && (
          <FormControl>
            <FormLabelWithInfoTooltip
              label={"Message body"}
              tip={messageBodyTooltip}
            />
            <Textarea
              {...register(
                "credentials.configuration.smart_handoff.message_body"
              )}
            />
          </FormControl>
        )}

        {isTemplateType && (
          <Stack spacing={2}>
            <Flex gap={2}>
              <FormControl>
                <FormLabel>Template name</FormLabel>
                <Input
                  placeholder="Enter template name"
                  {...register(
                    "credentials.configuration.smart_handoff.whatsapp.template_name"
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Language code</FormLabel>
                <Input
                  placeholder="en"
                  {...register(
                    "credentials.configuration.smart_handoff.whatsapp.language_code"
                  )}
                />
              </FormControl>
            </Flex>

            <FormControl>
              <FormLabel>
                Parameters{" "}
                <IconButton
                  isRound={true}
                  colorScheme="blue"
                  variant="outline"
                  icon={<AddIcon />}
                  size={"xs"}
                  onClick={handleAddParameter}
                />
              </FormLabel>

              <Stack spacing={2}>
                {bodyParameters &&
                  bodyParameters.map((param, index) => (
                    <Flex
                      key={index}
                      justifyContent={"center"}
                      alignItems={"center"}
                      gap={2}
                    >
                      <Input
                        placeholder="Enter template parameter"
                        value={param}
                        onChange={(e) => {
                          const params = [...bodyParameters];
                          params[index] = e.target.value;
                          setValue(
                            "credentials.configuration.smart_handoff.whatsapp.body_parameters",
                            params
                          );
                          setBodyParameters(params);
                        }}
                      />
                      <IconButton
                        isRound={true}
                        colorScheme="blue"
                        variant="outline"
                        aria-label="Add Field"
                        icon={<MinusIcon />}
                        size={"xs"}
                        onClick={() => handleRemoveParameter(index)}
                      />
                    </Flex>
                  ))}
              </Stack>
            </FormControl>
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
